import React, { useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Send } from 'lucide-react';
import { api } from '../lib/api';

const EMPTY = { name: '', email: '', phone: '', company: '', learn: '', format: 'either', people: 1, preferred_dates: '', location: '', machine: '', website: '' };
const inputCls = 'mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:border-brand-primary';
const FORMATS = [['onsite', 'On-site at my location'], ['remote', 'Remote / online'], ['either', 'Not sure — recommend one']];

const L = ({ label, children, span = '' }) => (
    <label className={`block ${span}`}><span className="label-caps text-xs text-neutral-500">{label}</span>{children}</label>
);

const TrainingForm = ({ courses, picked, setPicked }) => {
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

    const submit = async (e) => {
        e.preventDefault();
        if (f.learn.trim().length < 10) { setError('Tell us a little more about what you want to learn (at least a sentence).'); return; }
        setBusy(true); setError('');
        try {
            const { data } = await api.post('/training/request', { ...f, people: Number(f.people) || 1, course_ids: picked });
            if (window.trackConversion) window.trackConversion('training_request', { people: f.people, format: f.format });
            setDone(data.request_number);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === 'string' ? d : 'Please check the form — some fields are missing or invalid.');
        }
        setBusy(false);
    };

    if (done) {
        return (
            <div className="border border-emerald-200 bg-emerald-50 p-8" data-testid="training-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <h3 className="mt-4 font-display font-black uppercase text-3xl text-neutral-900">Request received</h3>
                <p className="mt-2 text-neutral-700">Request <b data-testid="training-request-number">{done}</b>. We&apos;ll get back to you within one business day with a training plan and price.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="training-form">
            <input type="text" value={f.website} onChange={upd('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            {courses.length > 0 && (
                <div className="sm:col-span-2">
                    <span className="label-caps text-xs text-neutral-500">Interested in (optional)</span>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="training-course-picks">
                        {courses.map((c) => (
                            <button type="button" key={c.id} onClick={() => toggle(c.id)} data-testid={`training-pick-${c.id}`}
                                className={`px-3 py-2 text-sm border transition-colors ${picked.includes(c.id) ? 'bg-neutral-900 text-on-dark border-neutral-900' : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-900'}`}>
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <L label="What do you want to learn? *" span="sm:col-span-2">
                <textarea required rows={4} value={f.learn} onChange={upd('learn')} className={inputCls} data-testid="training-learn"
                    placeholder="e.g. Safe setup and nozzle choice for cleaning food production lines, plus basic maintenance of our IC310S." />
            </L>
            <L label="Format">
                <select value={f.format} onChange={upd('format')} className={inputCls} data-testid="training-format">
                    {FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </L>
            <L label="Number of people"><input type="number" min={1} max={200} value={f.people} onChange={upd('people')} className={inputCls} data-testid="training-people" /></L>
            <L label="Preferred dates"><input value={f.preferred_dates} onChange={upd('preferred_dates')} placeholder="e.g. week of July 14, weekdays" className={inputCls} data-testid="training-dates" /></L>
            <L label="Location (city, state)"><input value={f.location} onChange={upd('location')} placeholder="e.g. Raleigh, NC" className={inputCls} data-testid="training-location" /></L>
            <L label="Machine you use or own" span="sm:col-span-2"><input value={f.machine} onChange={upd('machine')} placeholder="e.g. Cold Jet i3, IC310S, or none yet" className={inputCls} data-testid="training-machine" /></L>
            <L label="Full name *"><input required value={f.name} onChange={upd('name')} className={inputCls} data-testid="training-name" /></L>
            <L label="Company"><input value={f.company} onChange={upd('company')} className={inputCls} data-testid="training-company" /></L>
            <L label="Email *"><input required type="email" value={f.email} onChange={upd('email')} className={inputCls} data-testid="training-email" /></L>
            <L label="Phone *"><input required type="tel" value={f.phone} onChange={upd('phone')} className={inputCls} data-testid="training-phone" /></L>
            {error && <p className="sm:col-span-2 text-sm text-red-600 flex gap-2" data-testid="training-error"><AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{error}</p>}
            <button type="submit" disabled={busy}
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-6 py-4 label-caps transition-colors"
                data-testid="training-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send my training request
            </button>
        </form>
    );
};

export default TrainingForm;
